import { useState } from "react";
import { PencilSimple, RocketLaunch, Trash, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";
import { apiError, formatBytes, formatDate, formatNumber, releasedAt } from "@/lib/format";

const Badge = ({ cls, children, testId }) => <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${cls}`} data-testid={testId}>{children}</span>;

const EditPanel = ({ r, onDone }) => {
  const [notes, setNotes] = useState(r.release_notes);
  const [force, setForce] = useState(r.force_update);
  const [date, setDate] = useState(r.release_date || (r.created_at || "").slice(0, 10));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await adminApi.patch(`/admin/apks/${r.id}`, { release_notes: notes, force_update: force, release_date: date });
      toast.success("Release updated");
      onDone(true);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4 pt-4 border-t space-y-3" style={{ borderColor: "rgba(255,255,255,.06)" }} data-testid={`edit-panel-${r.version_code}`}>
      <label htmlFor={`e-notes-${r.id}`} className="block text-xs tracking-[0.2em] uppercase text-slate-500">Release notes</label>
      <textarea id={`e-notes-${r.id}`} rows={4} className="field" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`edit-notes-${r.version_code}`} />
      <label htmlFor={`e-date-${r.id}`} className="block text-xs tracking-[0.2em] uppercase text-slate-500">Release date</label>
      <input id={`e-date-${r.id}`} type="date" required className="field [color-scheme:dark]" value={date} onChange={(e) => setDate(e.target.value)} data-testid={`edit-date-${r.version_code}`} />
      <label className="flex items-center gap-3 text-sm text-slate-300 cursor-pointer">
        <input type="checkbox" className="accent-[#00BFFF] w-5 h-5" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid={`edit-force-${r.version_code}`} /> Force update
      </label>
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={busy} onClick={save} className="btn-primary !min-h-[44px] !py-2 text-sm" data-testid={`edit-save-${r.version_code}`}>Save changes</button>
        <button type="button" onClick={() => onDone(false)} className="btn-ghost !min-h-[44px] !py-2 text-sm" data-testid={`edit-cancel-${r.version_code}`}>Cancel</button>
      </div>
    </div>
  );
};

export const ReleaseRow = ({ r, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const act = async (fn, msg) => {
    setBusy(true);
    try {
      const { data } = await fn();
      toast.success(data.message || msg);
      onChange();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const setLive = () => act(() => adminApi.post(`/admin/apks/${r.id}/set-live`), "Now LIVE");
  const del = () => window.confirm(`Delete v${r.version_name} (#${r.version_code})? This cannot be undone.`) && act(() => adminApi.delete(`/admin/apks/${r.id}`), "Deleted");

  return (
    <li className="glass rounded-2xl p-4 sm:p-5" data-testid={`release-row-${r.version_code}`}>
      <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-display text-lg font-bold">v{r.version_name}</span>
            <span className="font-mono-alt text-xs text-slate-500">#{r.version_code}</span>
            {r.is_live ? <Badge cls="bg-[#00BFFF]/15 text-[#7DDCFF] border-[#00BFFF]/30" testId={`live-badge-${r.version_code}`}>LIVE</Badge> : <Badge cls="bg-white/5 text-slate-400 border-white/10" testId={`archived-badge-${r.version_code}`}>Archived</Badge>}
            {r.force_update && <Badge cls="bg-red-500/15 text-red-300 border-red-400/30 inline-flex items-center gap-1" testId={`force-badge-${r.version_code}`}><Lightning size={10} weight="fill" aria-hidden="true" />Force</Badge>}
          </div>
          <p className="text-xs text-slate-500 mt-1">{formatDate(releasedAt(r))} · {formatBytes(r.size)} · <span data-testid={`downloads-${r.version_code}`}>{formatNumber(r.downloads)}</span> downloads</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setEditing((e) => !e)} className="btn-ghost !min-h-[44px] !py-2 !px-4 text-sm" aria-expanded={editing} data-testid={`edit-btn-${r.version_code}`}><PencilSimple size={16} aria-hidden="true" /> Edit</button>
          {!r.is_live && (
            <>
              <button type="button" disabled={busy} onClick={setLive} className="btn-ghost !min-h-[44px] !py-2 !px-4 text-sm text-[#7DDCFF]" data-testid={`set-live-btn-${r.version_code}`}><RocketLaunch size={16} aria-hidden="true" /> Set LIVE</button>
              <button type="button" disabled={busy} onClick={del} className="btn-ghost !min-h-[44px] !py-2 !px-4 text-sm text-red-300 hover:!border-red-400/50" data-testid={`delete-btn-${r.version_code}`}><Trash size={16} aria-hidden="true" /> Delete</button>
            </>
          )}
        </div>
      </div>
      {editing && <EditPanel r={r} onDone={(changed) => { setEditing(false); changed && onChange(); }} />}
    </li>
  );
};
